import {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  ReactNode,
} from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import { roles, solutionTypes } from "../utils/constants";
import { TermsOfUseModal } from "../components/TermsOfUseModal";
import { IPlan } from "../modules/Admin/pages/Plans/dtos";
import { IUserTerms } from "../modules/Licensee/components/SignatureInfoModal/mocks";

export interface ITermosUso {
  id: string;
  name: string;
  version: string;
  document_link: string;
  type: string;
}

interface ILicensee {
  id: string;
  name: string;
  completed_onboard: boolean;
  signature: ISignature[];
  signature_status?: "active" | "blocked" | "cancelled";
  active_signature?: ISignature;
  country: string;
  state: string;
  city: string;
  contract: string;
  situation: string;
  manager_name: string;
  phone: string;
  document_number: string;
  person_type: string;
  instagram: string;
  created_at: string;
  is_blocked: boolean;
  is_fixpoint: boolean;
  is_test: boolean;
  ordered_initial_kit: boolean;

  company_name?: string | null;
  email: string;
  user_term_id: string;
  provider_id: string;
  locator?: {
    id?: string;
    name?: string;
    phone?: string;
    is_public?: boolean;
    use_billing_contact?: boolean;
    picture_link?: string;
  };
}

interface ITermsAcceptance {
  ip_address: string;
  signed_at: Date;
  document: string;
}

export interface ISignature {
  id: string;
  plan: IPlan;
  plans?: IPlan;
  is_activated?: boolean;
  end_date: Date;
  created_at: Date;
  user_terms: ITermosUso;
  terms_acceptance: ITermsAcceptance;
  payment_value: string;
  payment_recurrency: string;
}

interface IOnboardContext {
  licensee?: ILicensee;
  setLicensee: (licensee: any) => void;
  loadLicensee: () => void;
  loadingLicensee: boolean;
  solutionsWithAccess: string[];
  setSolutionsWithAccess: (solutionsNameKey: any) => void;
  checkBlocked: (pathname: string) => {
    message?: string;
    hasAccess: boolean;
  };
  planHasExpired: boolean;
  isSubscriptionBlocked: boolean;
  isSubscriptionCancelled: boolean;
  signatureStatus?: "active" | "blocked" | "cancelled";
  activePlan?: IPlan;
  isFixiter: boolean;
  isLicenseeWithPrinter: boolean;
  hasProvider: boolean;
  canPlaceOrders: boolean;
}

interface IUserDocument {
  document: ITermosUso | undefined,
  document_acceptance: ITermsAcceptance | undefined;
}

const OnboardContext = createContext<IOnboardContext>({} as IOnboardContext);

const OnboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [licensee, setLicensee] = useState<ILicensee>();
  const [loadingLicensee, setLoadingLicensee] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState(false);
  const [solutionsWithAccess, setSolutionsWithAccess] = useState<string[]>([]);
  const { user, signOut } = useAuth();
  const [userDocument, setUserDocument] = useState<IUserDocument>()

  const checkBlocked = (pathname: string) => {
    let pathnameArr = pathname.split("/");
    const solution_type = pathnameArr.pop();
    const solution_key = pathnameArr.pop();

    if (!licensee) {
      return {
        hasAccess: true,
      };
    }

    if (!licensee?.signature) {
      return {
        message: "",
        hasAccess: false,
      };
    }

    if (
      solution_type &&
      solution_type === solutionTypes.custom &&
      !licensee.signature[0]?.plan.custom_orders
    ) {
      return {
        message: "",
        hasAccess: false,
      };
    }

    if (
      solution_type &&
      solution_type === solutionTypes.standard &&
      !licensee.signature[0]?.plan.standard_orders
    ) {
      return {
        message: "",
        hasAccess: false,
      };
    }

    if (solution_key && !solutionsWithAccess.includes(solution_key)) {
      return {
        message: "",
        hasAccess: false,
      };
    }

    return {
      hasAccess: true,
    };
  };

  async function getPendingDocumentsToSign(type: string, locale: string) {
    try {
      await api.get(`/user_terms/validate`, {
        params: {
          type,
          locale,
        },
      }).then((response) => {
        setUserDocument(response.data)
      });

    } catch (error) {
      console.log(error);
      return null;
    }
  }

  useEffect(() => {
    if (
      user &&
      (user?.role === roles.fixiter ||
        user?.role === roles.licensee)
    ) {
      getPendingDocumentsToSign('user_terms', 'pt-BR')
    }
  }, [user])


  const loadLicensee = () => {
    if (
      user &&
      (user?.role === roles.fixiter ||
        user?.role === roles.licensee)
    ) {
      setLoadingLicensee(true);
      api
        .get(`/licensees/user/onboard`)
        .then((response) => {
          setLicensee(response.data);
        })
        .catch((error) => {
          console.error("Erro ao carregar dados do licenciado:", error);
        })
        .finally(() => {
          setLoadingLicensee(false);
        });
    } else {
      setLicensee(undefined);
      setLoadingLicensee(false);
    }
  };

  useEffect(() => {
    if (licensee) {
      api
        .get(`/signatures/solutions`)
        .then((response) => {
          setSolutionsWithAccess(
            Array.isArray(response.data)
              ? response.data.map((s: any) => s.name_key)
              : []
          );
        })
        .catch(() => {
          setSolutionsWithAccess([]);
        });
    }
  }, [licensee]);

  const { isSubscriptionBlocked, isSubscriptionCancelled, signatureStatus } = useMemo(() => {
    const targetLicensee = licensee || (user as any)?.person?.licensee || (user as any)?.licensee;

    const isSigActive = (s: any) =>
      Boolean(s) &&
      (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
      s.status !== "cancelled" &&
      s.status !== "canceled";

    const sortSignaturesDesc = (list: any[]) =>
      [...list].sort((a, b) => {
        const tA = new Date(a?.created_at || a?.start_date || 0).getTime();
        const tB = new Date(b?.created_at || b?.start_date || 0).getTime();
        return (isNaN(tB) ? 0 : tB) - (isNaN(tA) ? 0 : tA);
      });

    // 1. Prioridade máxima: signature_status autoritativo vindo do backend
    const rawStatus =
      targetLicensee?.signature_status ||
      (user as any)?.signature_status ||
      (targetLicensee as any)?.signatureStatus;

    if (rawStatus === "blocked") {
      return { isSubscriptionBlocked: true, isSubscriptionCancelled: false, signatureStatus: "blocked" as const };
    }
    if (rawStatus === "cancelled") {
      return { isSubscriptionBlocked: false, isSubscriptionCancelled: true, signatureStatus: "cancelled" as const };
    }
    if (rawStatus === "active") {
      if (targetLicensee?.active_signature && !isSigActive(targetLicensee.active_signature)) {
        return { isSubscriptionBlocked: false, isSubscriptionCancelled: true, signatureStatus: "cancelled" as const };
      }
      return { isSubscriptionBlocked: false, isSubscriptionCancelled: false, signatureStatus: "active" as const };
    }

    // 2. Se ainda não há dados do licenciado carregados
    if (!targetLicensee) {
      return { isSubscriptionBlocked: false, isSubscriptionCancelled: false, signatureStatus: undefined };
    }

    // 3. Fallback defensivo com regra das 2 condições:
    // a) Licenciado bloqueado diretamente
    if (targetLicensee.is_blocked || (user as any)?.person?.licensee?.is_blocked) {
      return { isSubscriptionBlocked: true, isSubscriptionCancelled: false, signatureStatus: "blocked" as const };
    }

    const rawSig = targetLicensee.signature;
    const sigList = sortSignaturesDesc(Array.isArray(rawSig) ? rawSig : rawSig ? [rawSig] : []);

    // b) Assinatura ativa (sem end_date ou com end_date >= NOW())
    const activeSig =
      (isSigActive(targetLicensee.active_signature) ? targetLicensee.active_signature : undefined) ||
      sigList.find(isSigActive);

    if (activeSig) {
      if (activeSig.status === "blocked" || (activeSig as any)?.is_blocked) {
        return { isSubscriptionBlocked: true, isSubscriptionCancelled: false, signatureStatus: "blocked" as const };
      }
      return { isSubscriptionBlocked: false, isSubscriptionCancelled: false, signatureStatus: "active" as const };
    }

    // c) Sem assinatura vigente (inclusive lista vazia ou todas expiradas) -> Cancelada
    return { isSubscriptionBlocked: false, isSubscriptionCancelled: true, signatureStatus: "cancelled" as const };
  }, [licensee, user]);

  const planHasExpired = isSubscriptionBlocked || isSubscriptionCancelled;

  useEffect(() => {
    if (user) {
      loadLicensee();
    }
  }, [user]);

  const activePlan = useMemo((): IPlan | undefined => {
    const targetLicensee = licensee || (user as any)?.person?.licensee || (user as any)?.licensee;
    const isSigActive = (s: any) =>
      Boolean(s) &&
      (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
      s.status !== "cancelled" &&
      s.status !== "canceled";

    const rawSig = targetLicensee?.signature;
    const sigList = (Array.isArray(rawSig) ? [...rawSig] : rawSig ? [rawSig] : []).sort((a: any, b: any) => {
      const tA = new Date(a?.created_at || a?.start_date || 0).getTime();
      const tB = new Date(b?.created_at || b?.start_date || 0).getTime();
      return (isNaN(tB) ? 0 : tB) - (isNaN(tA) ? 0 : tA);
    });

    const activeSig =
      (isSigActive(targetLicensee?.active_signature) ? targetLicensee?.active_signature : undefined) ||
      sigList.find(isSigActive);

    return (
      activeSig?.plan ||
      (activeSig as any)?.plans ||
      sigList[0]?.plan ||
      sigList[0]?.plans
    );
  }, [licensee, user]);

  const isFixiter = useMemo((): boolean => {
    if (activePlan && typeof activePlan.has_printer === "boolean") {
      return activePlan.has_printer === false;
    }
    return user?.role === roles.fixiter;
  }, [user, activePlan]);

  const isLicenseeWithPrinter = useMemo((): boolean => {
    if (activePlan && typeof activePlan.has_printer === "boolean") {
      return activePlan.has_printer === true;
    }
    return user?.role === roles.licensee;
  }, [user, activePlan]);

  const hasProvider = useMemo((): boolean => {
    const targetLicensee: any =
      licensee || (user as any)?.person?.licensee || (user as any)?.licensee;
    const pid =
      targetLicensee?.provider_id ||
      targetLicensee?.provider?.id;
    return isFixiter && Boolean(pid);
  }, [isFixiter, licensee, user]);

  const canPlaceOrders = isLicenseeWithPrinter || hasProvider;

  return (
    <OnboardContext.Provider
      value={{
        licensee,
        setLicensee: (licensee: any) => {
          setLicensee(licensee);
        },
        loadLicensee,
        loadingLicensee,
        solutionsWithAccess,
        setSolutionsWithAccess: (solutionsNameKey: any) => {
          setSolutionsWithAccess([...solutionsWithAccess, solutionsNameKey]);
        },
        checkBlocked,
        planHasExpired,
        isSubscriptionBlocked,
        isSubscriptionCancelled,
        signatureStatus,
        activePlan,
        isFixiter,
        isLicenseeWithPrinter,
        hasProvider,
        canPlaceOrders,
      }}
    >
      {/* {openModal && (
        <Modal open={openModal}>
          <ChooseSignatureSolutions closeModal={() => toggleModal()} />
        </Modal>
      )} */}
      {!!user &&
        !!licensee &&
        !!userDocument?.document &&
        !userDocument.document_acceptance &&
        (
          <TermsOfUseModal
            user={user}
            userTerm={userDocument}
            signOut={signOut}
            loadLicensee={() => {
              loadLicensee()
              getPendingDocumentsToSign('user_terms', 'pt-BR')
            }}
          />
        )}

      {children}
    </OnboardContext.Provider>
  );
};

function useOnboard() {
  const context = useContext(OnboardContext);

  if (!context) {
    throw new Error("useOnboard must be used within an AuthProvider");
  }

  return context;
}

export { useOnboard, OnboardProvider };
